/**
 * `formula` — `FormulaValue` (`specs/M2-formula.md`, `[09§A.7 formula]`, `[for§*]`).
 *
 * *"Formulas are computed server-side and cannot be written via the API."* / *"The `text` and
 * `value` fields are not populated for formula columns."* / *"Formula columns do not support
 * filtering via `items_page`."* Every one of the 146 sampled cells confirms it: `value` null,
 * `text` `""`, and only `display_value` ever carried anything `[cvs§2 formula]`.
 *
 * The **expression** is readable, and is the whole migration: `settings.formula`. Two archived
 * columns have `settings = {}` — a formula column with **no** expression, which must render an
 * empty cell rather than an error `[for§5]`.
 *
 * We materialise `{display_value}` into `cell_values` so the grid, sort, group-by and CSV all
 * work without re-evaluating per read, while `/v2` still answers `value: null` / `text: ""`
 * exactly as monday does. `packages/formula` owns the language; this folder owns the bridge
 * from a `{ref}` to a sibling cell — including the one rule the census pins down: **inside a
 * formula a status is its label text, not its index** `[for§5]`, which is the opposite of what
 * automations and `column_values.value` use.
 */
import { z } from 'zod';
import { evaluateFormula, parseFormula, referencedColumns } from '@elizmonday/formula';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { textShadow } from '../helpers.js';
import { asColumnId } from '../../ids.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const formulaValueSchema = z.looseObject({
    display_value: z.string(),
    /** Set when the expression could not be evaluated; the cell renders monday's ⚠ + tooltip. */
    error: z.string().nullable().optional(),
});
import { formulaFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const formulaSettingsSchema = z.looseObject({
    /** The expression. `undefined` on a column where none was ever entered `[for§5]`. */
    formula: z.string().optional(),
    hide_footer: z.boolean().optional(),
});
/** The `{column}` references an expression makes, for `column_dependencies`. */
export function formulaReferences(settings) {
    try {
        return referencedColumns(parseFormula(settings.formula ?? null));
    }
    catch {
        // A formula that does not parse depends on nothing; the cell renders the syntax error.
        return [];
    }
}
/** Turns one resolved sibling cell into a formula value. */
export function formulaValueOf(source, field) {
    if (source === undefined)
        return undefined;
    if (field !== undefined) {
        const value = source.fields?.[field] ?? source.fields?.[field.toLowerCase()];
        if (value === undefined)
            return null;
        if (typeof value === 'number')
            return value;
        if (value === null)
            return null;
        return /^\d{4}-\d{2}-\d{2}/.test(value) ? { kind: 'date', iso: value.slice(0, 10) } : value;
    }
    if (source.date !== undefined && source.date !== null)
        return { kind: 'date', iso: source.date.slice(0, 10) };
    if (source.type === 'numbers' || source.type === 'rating') {
        return source.number ?? null;
    }
    // `status`, `text`, `dropdown`, … all read as their rendered text.
    return source.text === null || source.text === '' ? null : source.text;
}
/**
 * Evaluates a formula column for one item.
 *
 * `cells` is keyed by **column id**, and each entry also carries its title so `{Numbers}` —
 * the by-title spelling the sandbox's own formula uses — resolves too.
 */
export function evaluateFormulaColumn(settings, cells, options = {}) {
    /* P-300: settings can be missing on a column the client received in another shape; no
       settings is no formula, never a throw from inside a cell. */
    const expression = settings?.formula ?? null;
    if (typeof expression !== 'string' || expression.trim() === '')
        return null;
    const byTitle = new Map();
    for (const [columnId, source] of Object.entries(cells)) {
        if (source.title !== undefined)
            byTitle.set(source.title.toLowerCase(), columnId);
    }
    const result = evaluateFormula(expression, {
        now: options.now ?? new Date(),
        timezone: options.timezone ?? 'UTC',
        resolveColumn(ref, field) {
            // By id first (what `settings.formula` stores), then by title (what the builder shows).
            const source = cells[ref] ?? cells[byTitle.get(ref.toLowerCase()) ?? ''];
            return formulaValueOf(source, field);
        },
    });
    if (result.error !== null)
        return { display_value: '', error: result.error };
    return { display_value: result.display };
}
export const formulaColumn = defineColumnType({
    type: 'formula',
    title: 'Formula',
    description: 'A value computed from the other columns. Read-only.',
    apiFragmentName: 'FormulaValue',
    legacyTypeName: 'formula',
    idPrefix: 'formula',
    valueSchema: formulaValueSchema,
    settingsSchema: formulaSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: formulaSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    /** *"Formula columns do not support filtering via `items_page`"* `[09§A.7 formula]`. */
    filterConditions: formulaFilterConditions,
    filterOperators: operatorsOf(formulaFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    /** monday returned `""`, never null, for every sampled formula cell `[cvs§2 formula]`. */
    emptyText: '',
    computed: {
        dependsOn(column, settings) {
            const parsed = formulaSettingsSchema.parse(settings ?? {});
            return formulaReferences(parsed).map((reference) => ({
                kind: 'column',
                boardId: column.boardId,
                // A by-title reference is resolved to an id by the service before the row is written;
                // an unresolved one is kept verbatim so the dependency is at least visible.
                columnId: asColumnId(reference.ref),
            }));
        },
        compute(ctx) {
            const cells = (ctx.cells ?? {});
            const settings = ctx.settings ?? {};
            return evaluateFormulaColumn(settings, cells, { now: ctx.now });
        },
        materialise: 'always',
        /** monday: `value` null, `text` `""`; only `display_value` `[cvs§2 formula]`. */
        api: { value: 'null', text: 'empty' },
    },
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        // *"Formulas are computed server-side and cannot be written via the API"* `[09§A.7]`.
        throw new ColumnValueError('formula', 'a formula is computed and cannot be written');
    },
    /** monday's `text` for a formula is `""` — the computed value is only in `display_value`. */
    toText() {
        return '';
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        const numeric = Number(value.display_value);
        return {
            text: textShadow(value.display_value),
            number: value.display_value !== '' && Number.isFinite(numeric) ? numeric : null,
            date: /^\d{4}-\d{2}-\d{2}$/.test(value.display_value)
                ? new Date(`${value.display_value}T00:00:00Z`)
                : null,
        };
    },
    isEqual(a, b) {
        return (a?.display_value ?? null) === (b?.display_value ?? null) && (a?.error ?? null) === (b?.error ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { display_value: value.display_value };
    },
    fromMondayValue() {
        // *"Migration: re-evaluate ourselves, never import the value"* `[cvs§2 formula]`.
        return null;
    },
    toMondayValue() {
        return null;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        const parsed = formulaSettingsSchema.parse(raw ?? {});
        // monday keeps a trailing newline inside `settings.formula` (the "Age" column) `[for§5]`.
        return parsed;
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        return { display_value: value?.display_value ?? '' };
    },
    toWebhookValue() {
        return null;
    },
    /**
     * **Measured correction (round 5o).** `specs/M1-B4` §8.5 said a formula column offers no
     * operators; monday offers nine. It filters on the *rendered* result, which is exactly what
     * the `text_value` shadow holds once `packages/formula` has recomputed the cell — so the
     * default text table is the whole implementation.
     */
    filterSql(ctx) {
        return defaultFilterSql('text', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map